import React from 'react'
import Modal from '../../../ui_reusable/Modal'
import DatasetForm from './dataset_form/DatasetForm'
import {
  useDatasetsPartialUpdate,
  useDatasetsRetrieve,
} from '../../../../api/generated/endpoints/datasets/datasets'
import type {
  Dataset,
  PatchedDatasetUpdateRequest,
} from '../../../../api/generated/models'
import {
  validateForRequest,
  validateAndLog,
} from '../../../../utils/zodDebugger'
import {
  datasetsPartialUpdateBody,
  datasetsPartialUpdateResponse,
} from '../../../../api/generated/validation/datasets/datasets.zod'
import { convertFilesToBase64, hasFiles } from '../../../../utils/fileConverter'
import { useQueryClient } from '@tanstack/react-query'
import UniversalLoader from '../../../ui_reusable/UniversalLoader'

// Shared types
import type { FormSubmitResult, FileInfo } from './types/DatasetHandlingTypes'

interface EditDatasetProps {
  dataset: Dataset
  open: boolean
  onClose: () => void
}

const EditDataset: React.FC<EditDatasetProps> = ({
  dataset,
  open,
  onClose,
}) => {
  const queryClient = useQueryClient()

  const { data: detailResponse, isLoading: isDetailLoading } =
    useDatasetsRetrieve(String(dataset.id), {
      query: { enabled: open && !!dataset.id },
    })
  const detailDataset = (detailResponse?.data as Dataset | undefined) ?? dataset

  const updateMutation = useDatasetsPartialUpdate({
    mutation: {
      onSuccess: () => {
        // Invalidate driver list queries to refresh the databook
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/with-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/without-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/'],
        })
      },
      onError: (error) => {
        console.error('Failed to update dataset:', error)
      },
    },
  })

  const handleSubmit = async (
    formData: Partial<any>,
    fileInfo: FileInfo | null,
  ): Promise<FormSubmitResult> => {
    try {
      // Prepare the request data - similar to original but with proper field processing
      const { file, ...cleanFormData } = formData

      // Convert empty strings to undefined for optional fields
      const processedFormData = Object.fromEntries(
        Object.entries(cleanFormData).map(([key, value]) => {
          if (
            value === '' &&
            [
              'client_id',
              'influence',
              'investment',
              'description',
              'source',
            ].includes(key)
          ) {
            return [key, undefined]
          }
          if (key === 'markets' && Array.isArray(value) && value.length === 0) {
            return [key, undefined]
          }
          return [key, value]
        }),
      )

      // Build request data - include file if present (like original approach)
      let requestData: any = { ...processedFormData }

      // Transform markets field to match API expectations
      if (requestData.markets && Array.isArray(requestData.markets)) {
        // Convert markets array to market_ids array of numbers
        requestData.market_ids = requestData.markets.map((id: string) =>
          parseInt(id, 10),
        )
        delete requestData.markets // Remove the old field
      }

      // Transform aggregation field to match API expectations
      if (requestData.aggregation) {
        requestData.aggregation_method = requestData.aggregation
        delete requestData.aggregation
      }

      // No field transformation needed for these fields as they already match the backend:
      // - part_of_media_channel
      // - apply_smoothing
      // - media_input_category
      // - media_channel_lag
      // - media_channel_constraint
      // - media_roi_assumption
      // - category

      // Remove computed fields that are calculated by the backend from the uploaded file
      // These should not be sent in the PATCH request as the backend will recalculate them
      delete requestData.total_observations
      delete requestData.included_observations
      delete requestData.start_at
      delete requestData.end_at
      delete requestData.quality
      delete requestData.status_text
      delete requestData.interval

      // Add file if present - ensure proper type for PatchedDatasetUpdateRequest
      if (fileInfo?.blobFile instanceof File) {
        requestData.file = fileInfo.blobFile
      } else if (
        fileInfo &&
        (fileInfo.blobFile as any)?.blobFile instanceof File
      ) {
        requestData.file = (fileInfo.blobFile as any).blobFile
      }
      // For updates, we don't need to set file to null if not provided
      // (it's optional in PatchedDatasetUpdateRequest)

      // Convert files to base64 for JSON transmission
      if (hasFiles(requestData)) {
        requestData = await convertFilesToBase64(requestData)
      }

      // Validate request data before sending
      const validatedRequestData = validateForRequest(
        datasetsPartialUpdateBody,
        requestData,
        'EditDataset',
      )

      // Send as JSON request
      const response = await updateMutation.mutateAsync({
        id: String(detailDataset.id),
        data: validatedRequestData as PatchedDatasetUpdateRequest,
      })

      // Validate response data
      const validatedResponse = validateAndLog(
        datasetsPartialUpdateResponse,
        response,
        'EditDataset',
      )

      if (!validatedResponse) {
        console.warn(
          'EditDataset: API response validation failed, but operation may have succeeded',
        )
      }

      return { success: true }
    } catch (error: any) {
      console.error('Update failed:', error)

      // Extract error message and convert to string for display
      let errorMessage = 'Update failed'
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500) - Check backend logs for details'
      } else if (error.response?.data?.detail) {
        errorMessage = String(error.response.data.detail)
      } else if (error.response?.data?.message) {
        errorMessage = JSON.stringify(error.response.data.message)
      } else if (error.message) {
        errorMessage = String(error.message)
      }

      return {
        success: false,
        error: errorMessage,
      }
    }
  }

  const handleSuccess = () => {
    // Close modal after brief delay
    setTimeout(() => {
      onClose()
    }, 100)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Dataset"
      style={{ width: 'auto', height: 'auto' }}
    >
      <Modal.Body>
        <div style={{ width: '75rem' }}>
          {isDetailLoading ? (
            <UniversalLoader show content="Loading dataset..." />
          ) : (
            <DatasetForm
              onSubmit={handleSubmit}
              initialData={detailDataset}
              submitButtonText="UPDATE DATASET"
              successMessage="Dataset updated successfully!"
              isEdit={true}
              onSuccess={handleSuccess}
            />
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default EditDataset
